<script lang="ts">
  import {
    tasks, det, canEdit, openAddActivity,
    allowWeekendWork, setAllowWeekendWork,
    projectStartDate, setProjectStartDate, leanView, floorDay, selectedWeek, boardSortKey,
    subcontractorColors, setSubcontractorColor, knownSubcontractors, projectPhases,
    bulkEditMode, bulkSelection, setBulkEditMode, clearBulkSelection, bulkSetPhase, bulkShiftDay, bulkUnpin,
  } from "../lib/stores";
  import { subcontractorColor, TAG_COLORS } from "../lib/subcontractors";
  import { exportPlanningWorkbook } from "../lib/excel";
  import { exportP6Xml } from "../lib/p6xml";
  import { exportWeekPlanningPdf } from "../lib/pdf";
  import { allTaskDays, boardDayCount, BOARD_SORT_LABEL, weeksOfPlan } from "../lib/board";
  import type { BoardSortKey } from "../lib/board";
  import { projectDayRangeLabel } from "../lib/projectDate";
  import { checkboxValue, inputValue } from "../lib/dom";
  import WbsPanel from "./WbsPanel.svelte";
  import BoardTable from "./BoardTable.svelte";
  import BoardDayList from "./BoardDayList.svelte";
  import TodayView from "./TodayView.svelte";
  import SummaryView from "./SummaryView.svelte";

  $: editable = $canEdit;

  function onWeekendWorkToggle(e: Event) {
    setAllowWeekendWork(checkboxValue(e));
  }
  function onStartDateChange(e: Event) {
    setProjectStartDate(inputValue(e));
  }
  /** Assigns the color and collapses the swatch popover it was picked from. */
  function pickSubcontractorColor(name: string, hex: string, e: Event) {
    setSubcontractorColor(name, hex);
    (e.currentTarget as HTMLElement).closest("details")?.removeAttribute("open");
  }

  $: result = $det;
  $: scheduleOk = result.ok;
  /** The two views that actually draw the sticky board, and so carry its controls. */
  $: isBoardView = $leanView === "plan" || $leanView === "week";
  /** Every view that lays cards out into cells — adds Today, where drag isn't offered
   *  but the same sort order still applies. */
  $: showSortControl = isBoardView || $leanView === "today";
  const SORT_KEYS: BoardSortKey[] = ["schedule", "wbs", "party", "subcontractor", "critical"];

  // Leaving the board view mid-bulk-edit (or losing edit rights) would strand the
  // toolbar showing a selection nothing can act on any more.
  $: if (!isBoardView || !editable) setBulkEditMode(false);
  $: selectedIds = [...$bulkSelection];

  let bulkTargetPhase = "";
  function applyBulkPhase() {
    if (!bulkTargetPhase) return;
    bulkSetPhase(selectedIds, bulkTargetPhase);
    bulkTargetPhase = "";
  }
  $: maxDay = result.ok ? boardDayCount(allTaskDays($tasks, result, $allowWeekendWork), result.projectEnd) : 1;
  $: allDays = Array.from({ length: maxDay }, (_, i) => i);
  /** Every week the plan covers, as the 7 day-indices each one owns. */
  $: weeks = weeksOfPlan(maxDay);
  $: lastWeek = Math.max(0, weeks.length - 1);

  // A null selection follows the clock, so the week view opens on the week actually
  // being worked rather than on week 1 of a plan that started a month ago.
  $: clockWeek = $floorDay == null ? 0 : Math.floor($floorDay / 7);
  $: weekIdx = Math.min(lastWeek, $selectedWeek ?? clockWeek);
  $: weekDays = weeks[weekIdx] ?? [];
  $: isThisWeek = $floorDay != null && weekIdx === clockWeek;

  function stepWeek(delta: number) {
    selectedWeek.set(Math.max(0, Math.min(lastWeek, weekIdx + delta)));
  }

  // "" means every subcontractor — the PDF export filters activities, equipment and
  // personnel down to just this one when set, so a subcontractor can be handed a
  // sheet that only shows their own work.
  let pdfSubcontractor = "";
  let exportingPdf = false;
  async function downloadWeekPlanningPdf() {
    if (exportingPdf) return;
    exportingPdf = true;
    try {
      await exportWeekPlanningPdf({ subcontractor: pdfSubcontractor || null });
    } finally {
      exportingPdf = false;
    }
  }

  const VIEWS = [
    { id: "plan", label: "Plan" },
    { id: "week", label: "Week" },
    { id: "today", label: "Today" },
    { id: "summary", label: "Summary" },
  ] as const;

  const DESCRIPTIONS: Record<string, string> = {
    plan: "One column per day grouped into weeks (weekends shaded, days 5–6 of each 7-day block), one swimlane per phase, one sticky per activity or milestone active that day — the same layout as the daily LEAN board. Stickies with a red edge sit on the critical path (zero slack); green ones are done, amber blocked.",
    week: "One week at a time with no sideways scrolling — the look-ahead for the crew and the subcontractors, downloadable as a one-page-per-week PDF to hand to them.",
    today: "One day at a time, sized to be read across the room — the planned site status, the equipment and people that day calls for, and every activity active on it grouped by phase. Mark each day's work done or blocked as the standup goes; unfinished work slides forward and pushes whatever depends on it.",
    summary: "The readout: when the changeover now finishes and whether that's moved against the baseline, which milestones are left and where they've drifted to, the activities currently holding the end date, and anything blocked — oldest first. No activity-level detail.",
  };

  const TITLES: Record<string, string> = {
    plan: "Daily board — by day & phase swimlane, deterministic (M) schedule",
    week: "Week board — look-ahead, with a PDF export for subcontractors",
    today: "Standup — one day at a time",
    summary: "Summary — forecast, milestones and what's driving the date",
  };
</script>

<div class="tab-panel group">
  <div class="group-head">
    <span class="group-tag lean">Lean planning</span>
    <span class="group-desc">{DESCRIPTIONS[$leanView]}</span>
  </div>

  <section>
    <div class="section-head">
      <div class="section-title">{TITLES[$leanView]}</div>
      <div class="board-settings">
        {#if editable}
          <button class="btn primary" type="button" on:click={() => openAddActivity()}>+ Add activity</button>
        {/if}
        <button class="btn" type="button" title="Activities, risks and WBS in one workbook — for offline review or as Monte Carlo input elsewhere" on:click={exportPlanningWorkbook}>Export to Excel</button>
        <button class="btn" type="button" title="Current schedule as P6 XML, for Import P6 XML or XER Data in Primavera Cloud/P6" on:click={exportP6Xml}>Export to Primavera (P6 XML)</button>
        <div class="lean-view-switch" role="group" aria-label="Board view">
          {#each VIEWS as v (v.id)}
            <button class="lean-view-btn" class:active={$leanView === v.id} type="button" on:click={() => leanView.set(v.id)}>
              {v.label}
            </button>
          {/each}
        </div>
        {#if editable && isBoardView}
          <button
            class="btn"
            class:primary={$bulkEditMode}
            type="button"
            title="Select several cards and move, unpin or re-lane them together, instead of one drag at a time."
            on:click={() => setBulkEditMode(!$bulkEditMode)}
          >
            {$bulkEditMode ? "Done selecting" : "Bulk edit"}
          </button>
        {/if}
        {#if showSortControl}
          <label class="board-setting" title="How cards within one day (and shift/phase) are ordered. Schedule order is the default — earliest-start first, so work already under way sits above a fresh start.">
            Sort by
            <select class="today-select" bind:value={$boardSortKey}>
              {#each SORT_KEYS as key (key)}
                <option value={key}>{BOARD_SORT_LABEL[key]}</option>
              {/each}
            </select>
          </label>
        {/if}
        <label class="board-setting" title="The first day of planned work — can be any day of the week, not just a Monday. Leave empty to keep the board in relative-day mode. With a date set, unfinished work can no longer be scheduled before it or in the past — anything not marked done by the end of its planned day moves to today and pushes its successors.">
          Start date
          <input type="date" value={$projectStartDate ?? ""} disabled={!editable} on:change={onStartDateChange} />
        </label>
        <label class="board-setting" title="Applies to the whole computed schedule — Input table, Analysis and Lean board — not just this view. An individual activity can override this in its own edit panel.">
          <input type="checkbox" checked={$allowWeekendWork} disabled={!editable} on:change={onWeekendWorkToggle} />
          Work can be done in the weekend
        </label>
        <div class="subc-color-editor" title="Colors the subcontractor tag on every card and Today-view chip — shared with everyone on this project.">
          {#each $knownSubcontractors as sc}
            {#if editable}
              <details class="subc-color-item">
                <summary>
                  <span class="subc-swatch" style="background:{subcontractorColor($subcontractorColors, sc)}"></span>
                  {sc}
                </summary>
                <div class="subc-color-grid" role="group" aria-label="Pick a color for {sc}">
                  {#each TAG_COLORS as c (c.hex)}
                    <button type="button" class="subc-swatch-btn" style="background:{c.hex}" title={c.name}
                      aria-label="{c.name} for {sc}"
                      aria-pressed={subcontractorColor($subcontractorColors, sc) === c.hex}
                      on:click={(e) => pickSubcontractorColor(sc, c.hex, e)}
                    ></button>
                  {/each}
                </div>
              </details>
            {:else}
              <span class="subc-color-item static">
                <span class="subc-swatch" style="background:{subcontractorColor($subcontractorColors, sc)}"></span>
                {sc}
              </span>
            {/if}
          {/each}
        </div>
      </div>
    </div>

    {#if $bulkEditMode && isBoardView}
      <div class="bulk-bar" role="toolbar" aria-label="Bulk edit selected cards">
        <span class="bulk-count">{selectedIds.length} selected</span>
        <label class="board-setting">
          Move to lane
          <select class="today-select" bind:value={bulkTargetPhase} disabled={!selectedIds.length} on:change={applyBulkPhase}>
            <option value="" disabled>Choose a lane…</option>
            {#each $projectPhases as p (p.name)}
              <option value={p.name}>{p.name}</option>
            {/each}
          </select>
        </label>
        <button class="btn" type="button" disabled={!selectedIds.length} on:click={() => bulkShiftDay(selectedIds, -1)}>‹ Day</button>
        <button class="btn" type="button" disabled={!selectedIds.length} on:click={() => bulkShiftDay(selectedIds, 1)}>Day ›</button>
        <button class="btn" type="button" disabled={!selectedIds.length} on:click={() => bulkUnpin(selectedIds)}>Unpin</button>
        <button class="btn" type="button" disabled={!selectedIds.length} on:click={clearBulkSelection}>Clear selection</button>
        <span class="bulk-hint">Click cards on the board below to select them.</span>
      </div>
    {/if}

    <WbsPanel />

    {#if $leanView === "today"}
      <TodayView />
    {:else if $leanView === "summary"}
      <SummaryView />
    {:else if $leanView === "week"}
      <div class="week-head">
        <div class="week-title">
          <div class="week-name">
            Week {weekIdx + 1}
            {#if isThisWeek}<span class="today-now">This week</span>{/if}
          </div>
          <div class="week-range">{projectDayRangeLabel($projectStartDate, weekDays)}</div>
        </div>
        <div class="today-controls">
          {#if $floorDay != null && !isThisWeek}
            <button class="today-jump" type="button" on:click={() => selectedWeek.set(null)}>This week</button>
          {/if}
          <select class="today-select" bind:value={pdfSubcontractor} title="Restrict the downloaded PDF to one subcontractor's own activities, equipment and personnel">
            <option value="">All subcontractors</option>
            {#each $knownSubcontractors as sc}
              <option value={sc}>{sc} only</option>
            {/each}
          </select>
          <button class="today-jump" type="button" disabled={exportingPdf} on:click={downloadWeekPlanningPdf}>
            {exportingPdf ? "Preparing PDF…" : "Download PDF"}
          </button>
          <button class="today-nav" type="button" title="Previous week" disabled={weekIdx <= 0} on:click={() => stepWeek(-1)}>‹</button>
          <button class="today-nav" type="button" title="Next week" disabled={weekIdx >= lastWeek} on:click={() => stepWeek(1)}>›</button>
        </div>
      </div>
      <div class="board-wide"><BoardTable days={weekDays} fit /></div>
      <div class="board-narrow"><BoardDayList days={weekDays} /></div>
    {:else}
      <div class="board-wide"><BoardTable days={allDays} /></div>
      <div class="board-narrow"><BoardDayList days={allDays} /></div>
    {/if}

    {#if isBoardView}
      <div class="lean-hint">
        Drag a sticky — or focus one and use <kbd>Alt</kbd>+<kbd>←</kbd>/<kbd>→</kbd> for days,
        <kbd>Alt</kbd>+<kbd>↑</kbd>/<kbd>↓</kbd> for phase — to reschedule it. That
        <strong>pins</strong> its earliest start to that day; predecessors can still push it later.
        Click the 📌 badge to unpin and go back to automatic scheduling. Status rows cycle
        not&nbsp;set → {"{on}"} → {"{off}"}, so a day nobody has decided on stays blank rather than
        reading as a decision. Green means nothing to do (trafo On, or boom with no
        requirement); amber calls for attention (trafo Off, or boom pinned Up/Down).
      </div>
    {/if}
  </section>
</div>
